import axios, { AxiosInstance, AxiosResponse } from 'axios';

const API_URL = (() => {
  const envUrl = import.meta.env.VITE_API_URL as string | undefined;
  if (envUrl && typeof envUrl === 'string' && envUrl.trim()) {
    return envUrl.trim();
  }
  if (import.meta.env.PROD && typeof window !== 'undefined') {
    return `${window.location.origin}/api`;
  }
  return 'http://localhost:8080/api';
})();

interface User {
  id: number;
  email: string;
  is_pro: number;
}

interface AuthResponse {
  token: string;
  user: User;
}

interface LoginData {
  email: string;
  password: string;
}

interface RegisterData {
  email: string;
  password: string;
}

interface GenerationRequest {
  lottery_slug: string;
  method: 'aleatorio' | 'balanceado' | 'frequencia' | 'atraso' | 'anti-repeticao' | 'manual' | 'premiei';
  numbers_per_game: number;
  games_count: number;
  manual_numbers?: number[][];
  vou_jogar?: boolean;
  draw_mode?: 'next' | 'specific';
}

interface GenerationResponse {
  generation_id: number;
  games: number[][];
  ticket_id?: number;
}

interface Lottery {
  id: number;
  slug: string;
  name: string;
  config: {
    type: string;
    range_min: number;
    range_max: number;
    min_numbers: number;
    max_numbers: number;
    description: string;
  };
}

interface Ticket {
  id: number;
  user_id: number;
  lottery_id: number;
  draw_id?: number;
  status: 'pending' | 'checked';
  created_at: string;
  checked_at?: string;
}

interface Notification {
  id: string;
  user_id: number;
  type: 'ticket' | 'alert' | 'system';
  status: 'pendente' | 'processando' | 'concluido' | 'cancelado';
  title: string;
  message: string;
  ticket_number?: string;
  data?: any;
  is_read: boolean;
  read_at?: string;
  created_at: string;
  updated_at: string;
}

interface Game {
  id: string;
  user_id: number;
  lottery_type: string;
  draw_number?: string;
  generation_id?: string;
  status: 'pending' | 'checked' | 'expired';
  numbers: number[];
  extra?: any;
  created_at: string;
  updated_at: string;
  hits?: number;
  prize?: number;
  results?: any[];
}

interface Draw {
  id: string;
  lottery_type: string;
  draw_number: string;
  draw_date: string;
  drawn_numbers: number[];
  extra?: any;
  prize_breakdown?: any;
  created_at: string;
}

interface Statistics {
  frequency?: any[];
  delay?: any[];
  patterns?: any;
  user_stats?: any;
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

  // Adicionar interceptor para incluir token nas requisições
  this.api.interceptors.request.use((config) => {
    const token = this.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Interceptor para lidar com respostas
  this.api.interceptors.response.use(
    (response) => {
      try {
        const ct = String(response.headers?.['content-type'] || '')
        const isHtml = ct.includes('text/html') || (typeof response.data === 'string' && response.data.trim().toLowerCase().startsWith('<!doctype html'))
        if (isHtml) {
          const err: any = new Error('Resposta HTML recebida da API — verifique VITE_API_URL (backend).')
          err.normalizedMessage = 'Resposta HTML recebida da API — verifique VITE_API_URL (backend).'
          err.response = response
          return Promise.reject(err)
        }
      } catch {}
      return response
    },
    (error) => {
      const data = error.response?.data;
      if (data && typeof data === 'object') {
        const msg = (data.messages && (data.messages.error || data.messages.message)) || data.message || data.error;
        if (msg) error.normalizedMessage = String(msg);
      } else if (typeof data === 'string') {
        error.normalizedMessage = data;
      }
      if (!error.normalizedMessage && error.response?.status === 401) {
        error.normalizedMessage = 'Credenciais inválidas';
      }
      if (!error.normalizedMessage && error.message) {
        error.normalizedMessage = error.message;
      }
      return Promise.reject(error);
    }
  );
}

  // Métodos de autenticação
  login(data: LoginData): Promise<AuthResponse> {
    return this.api.post('/auth/login', data).then(res => res.data);
  }

  register(data: RegisterData): Promise<AuthResponse> {
    return this.api.post('/auth/register', data).then(res => res.data);
  }

  getMe(): Promise<User> {
    return this.api.get('/auth/me').then(res => res.data);
  }

  // Métodos de loterias
  getLotteries(): Promise<Lottery[]> {
    return this.api.get('/lotteries').then(res => res.data);
  }

  getDraws(lotterySlug: string): Promise<any[]> {
    return this.api.get('/draws', { params: { lottery: lotterySlug } }).then(res => {
      const arr = res.data || [];
      return arr.map((d: any) => ({ ...d, lottery_type: lotterySlug }));
    });
  }

  // Métodos de geração de apostas
  generateBets(data: GenerationRequest): Promise<GenerationResponse> {
    return this.api.post('/generations', data).then(res => res.data);
  }

  getGenerationPdf(generationId: number): Promise<Blob> {
    return this.api.get(`/generations/${generationId}/pdf`, {
      responseType: 'blob'
    }).then(res => res.data);
  }

  getGenerationDebug(generationId: number): Promise<any> {
    return this.api.get(`/generations/${generationId}/pdfDebug`).then(res => res.data);
  }

  // Verificador Premiei
  verifyPremieiBet(data: { lottery_slug: string; selected_numbers: number[]; numbers_count: number; contest_ref?: string }): Promise<any> {
    return this.api.post('/premiei/verificar-aposta', {
      game_type: data.lottery_slug,
      selected_numbers: data.selected_numbers,
      numbers_count: data.numbers_count,
      contest_ref: data.contest_ref,
    }).then(res => res.data);
  }

  // Métodos de tickets
  getTickets(status?: 'pending' | 'checked'): Promise<Ticket[]> {
    const params = status ? { status } : {};
    return this.api.get('/tickets', { params }).then(res => res.data);
  }

  getTicket(id: number): Promise<Ticket> {
    return this.api.get(`/tickets/${id}`).then(res => res.data);
  }

  // Métodos de notificações
  getNotifications(filters?: any): Promise<{ data: Notification[], total: number, unread_count: number }> {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.is_read) params.append('is_read', filters.is_read);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    
    return this.api.get('/notifications', { params }).then(res => res.data);
  }

  getNotification(id: string): Promise<Notification> {
    return this.api.get(`/notifications/${id}`).then(res => res.data);
  }

  markNotificationAsRead(id: string): Promise<void> {
    return this.api.post(`/notifications/${id}/read`).then(res => res.data);
  }

  markNotificationAsReadPatch(id: string): Promise<void> {
    return this.api.patch(`/notifications/${id}/read`, { read: true }).then(res => res.data);
  }

  markAllNotificationsAsRead(): Promise<{ updated_count: number }> {
    return this.api.post('/notifications/mark-all-read').then(res => res.data);
  }

  deleteNotification(id: string): Promise<void> {
    return this.api.delete(`/notifications/${id}`).then(res => res.data);
  }

  // Métodos de jogos (tickets)
  getGames(filters?: any): Promise<{ data: Game[], pagination: any }> {
    const params = new URLSearchParams();
    if (filters?.lottery_type) params.append('lottery_type', filters.lottery_type);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    
    return this.api.get('/games', { params }).then(res => res.data);
  }

  getGame(id: string): Promise<Game> {
    return this.api.get(`/games/${id}`).then(res => res.data);
  }

  duplicateGame(id: string): Promise<Game> {
    return this.api.post(`/games/${id}/duplicate`).then(res => res.data);
  }

  checkGame(id: string): Promise<any> {
    return this.api.post(`/games/${id}/check`).then(res => res.data);
  }

  getGamePDF(id: string): Promise<Blob> {
    return this.api.get(`/games/${id}/pdf`, { responseType: 'blob' }).then(res => res.data);
  }

  deleteGame(id: string): Promise<void> {
    return this.api.delete(`/games/${id}`).then(res => res.data);
  }

  assignContest(id: string, drawNumber: string): Promise<{ assigned: boolean; auto_checked: boolean; hits: number; prize: number; draw_number: string }>{
    return this.api.post(`/games/${id}/contest`, { draw_number: drawNumber }).then(res => res.data);
  }

  checkGamesBatch(ids: string[]): Promise<{ checked: number; skipped: number }>{
    return this.api.post('/games/check-batch', { game_ids: ids }).then(res => res.data);
  }

  createGame(lotterySlug: string, numbers: number[], extra?: any): Promise<Game> {
    return this.api.post('/games', { lottery_slug: lotterySlug, numbers, extra }).then(res => res.data);
  }

  getGamesPDFBatch(ids: string[]): Promise<Blob> {
    return this.api.post('/games/pdf-batch', { game_ids: ids }, { responseType: 'blob' }).then(res => res.data);
  }

  // Métodos de resultados (draws)
  getResults(filters?: any): Promise<{ data: Draw[], pagination: any }> {
    if (filters?.lottery_type) {
      return this.getDraws(filters.lottery_type).then((arr: any[]) => ({ data: arr as any, pagination: { total: arr.length } }));
    }
    return Promise.all(['mega-sena','lotofacil','quina'].map((s)=>this.getDraws(s))).then(all => ({ data: ([] as any[]).concat(...all), pagination: { total: all.reduce((t,a)=>t+a.length,0) } }))
  }

  getDraw(id: string): Promise<Draw> {
    return this.api.get(`/results/${id}`).then(res => res.data);
  }

  syncDraws(lotteryType: string): Promise<{ imported: number }> {
    return this.api.post('/results/sync', { lottery_type: lotteryType }).then(res => res.data);
  }

  checkDrawGames(drawId: string): Promise<any> {
    return this.api.post(`/results/check-tickets`, { draw_id: drawId }).then(res => res.data);
  }

  adminCheckTickets(lotterySlug: string): Promise<{ checked_tickets: number; winners_found: number }> {
    return this.api.post('/admin/tickets/check', { lottery_slug: lotterySlug }).then(res => res.data);
  }

  getExternalLatest(lotterySlug: string): Promise<{ source: string; raw: any; normalized: any }> {
    return this.api.get('/external/latest', { params: { lottery_slug: lotterySlug } }).then(res => res.data);
  }

  // Métodos de estatísticas
  getFrequencyStats(filters?: any): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.lottery_type) params.append('lottery_type', filters.lottery_type);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    
    return this.api.get('/statistics/frequency', { params }).then(res => res.data);
  }

  getDelayStats(filters?: any): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.lottery_type) params.append('lottery_type', filters.lottery_type);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    
    return this.api.get('/statistics/delay', { params }).then(res => res.data);
  }

  getPatternStats(filters?: any): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.lottery_type) params.append('lottery_type', filters.lottery_type);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    
    return this.api.get('/statistics/patterns', { params }).then(res => res.data);
  }

  getDiaDeSorteStats(filters?: any): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    
    return this.api.get('/statistics/dia-de-sorte', { params }).then(res => res.data);
  }

  getTimemaniaStats(filters?: any): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    
    return this.api.get('/statistics/timemania', { params }).then(res => res.data);
  }

  getSuperSeteStats(filters?: any): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    
    return this.api.get('/statistics/super-sete', { params }).then(res => res.data);
  }

  getDuplaSenaStats(filters?: any): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    
    return this.api.get('/statistics/dupla-sena', { params }).then(res => res.data);
  }

  // Conveniência: agregador de estatísticas com fallback local
  async getStatistics(params: { lottery_type: string; period?: string; analysis_type?: string }): Promise<any> {
    const { lottery_type, period } = params
    const now = new Date()
    let startDate: string | undefined
    if (period === '30d') {
      const d = new Date(now); d.setDate(d.getDate() - 30); startDate = d.toISOString().slice(0,10)
    } else if (period === '90d') {
      const d = new Date(now); d.setDate(d.getDate() - 90); startDate = d.toISOString().slice(0,10)
    } else if (period === '1y') {
      const d = new Date(now); d.setFullYear(d.getFullYear() - 1); startDate = d.toISOString().slice(0,10)
    }

    const allDraws = await this.getDraws(lottery_type)
    const filtered = startDate ? allDraws.filter((d:any)=> new Date(d.draw_date) >= new Date(startDate as string)) : allDraws
    const nums: number[] = ([] as number[]).concat(...filtered.map((d:any)=> d.numbers || d.drawn_numbers || []))
    const freqMap = new Map<number, number>()
    nums.forEach(n=> freqMap.set(n, (freqMap.get(n)||0)+1))
    const most = Array.from(freqMap.entries()).sort((a,b)=> b[1]-a[1]).slice(0,10).map(([number, frequency])=>({number, frequency}))
    const least = Array.from(freqMap.entries()).sort((a,b)=> a[1]-b[1]).slice(0,10).map(([number, frequency])=>({number, frequency}))

    const patterns = {
      even_odd_ratio: nums.length ? (nums.filter(n=> n%2===0).length / nums.length) : 0,
      sum_average: nums.length ? (nums.reduce((a,b)=>a+b,0) / (filtered.length || 1)) : 0,
      repetition_rate: 0
    }

    // Buscar atrasos no backend para melhor qualidade
    const delayParams: any = new URLSearchParams()
    delayParams.append('lottery_type', lottery_type)
    delayParams.append('limit', '20')
    if (startDate) delayParams.append('start_date', startDate)
    const delayed = await this.api.get('/statistics/delay', { params: delayParams }).then(res => res.data).catch(()=>[])

    return { most_frequent: most, least_frequent: least, delayed_numbers: Array.isArray(delayed)? delayed: [], patterns }
  }

  getUserStats(filters?: any): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.user_id) params.append('user_id', filters.user_id);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    
    return this.api.get('/statistics/user', { params }).then(res => res.data);
  }

  getMockStats(lotteryType?: string): Promise<any> {
    const params = new URLSearchParams();
    if (lotteryType) params.append('lottery_type', lotteryType);
    
    return this.api.get('/statistics/mock', { params }).then(res => res.data);
  }

  // Métodos de pagamento
  createCheckout(userId: number): Promise<{ init_point: string }> {
    return this.api.post('/billing/checkout', { user_id: userId }).then(res => res.data);
  }

  // Premiei Express
  expressGenerate(params: { dezenas: number; apostas: number }): Promise<{ token: string; preview: string[][]; price_cents: number; price_formatted: string }>{
    return this.api.post('/express/generate', params).then(res => res.data);
  }

  expressPixCreate(unlockToken: string): Promise<{ txid: string; qr_code: string; copia_cola: string; expires_at: string }>{
    return this.api.post('/express/pix/create', { unlock_token: unlockToken }).then(res => res.data);
  }

  expressPixStatus(txid: string): Promise<{ status: 'pending'|'paid'|'expired'; full_games?: string[][] }>{
    return this.api.get('/express/pix/status', { params: { txid, ts: Date.now() } }).then(res => res.data);
  }

  expressPdf(token: string): Promise<Blob>{
    return this.api.get(`/express/pdf/${token}`, { responseType: 'blob' }).then(res => res.data);
  }

  expressWhatsappSend(token: string, phone: string): Promise<{ message: string }>{
    return this.api.post('/express/whatsapp/send', { token, phone }).then(res => res.data);
  }

  // Admin (sandbox): ativar PRO para usuário autenticado
  activatePro(): Promise<{ message: string }> {
    return this.api.post('/admin/activate-pro').then(res => res.data);
  }



  // Métodos de utilidade
  setToken(token: string): void {
    localStorage.setItem('token', token);
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  removeToken(): void {
    localStorage.removeItem('token');
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  logout(): void {
    this.removeToken();
  }

  // Método para verificar se usuário é PRO
  async isPro(): Promise<boolean> {
    try {
      const user = await this.getMe();
      return user.is_pro === 1;
    } catch {
      return false;
    }
  }

  extractErrorMessage(error: any): string {
    if (!error) return 'Erro inesperado';
    if (typeof error.normalizedMessage === 'string' && error.normalizedMessage.trim()) return error.normalizedMessage;
    const data = error.response?.data;
    if (data) {
      if (typeof data === 'string') return data;
      const msg = (data.messages && (data.messages.error || data.messages.message)) || data.message || data.error;
      if (msg) return String(msg);
    }
    if (error.response?.status === 401) return 'Credenciais inválidas';
    return 'Erro ao processar solicitação';
  }
}

export const apiService = new ApiService();
export default apiService;
